import { service } from '@ember/service';

import type Highcharts from 'highcharts';

import type { BlockThemeConfig, DChartInterface, Service } from 'webapp-types';
import { DataSelectType } from 'webapp/enums/enums';

import type { CustomColorMap } from 'webapp/models-custom/block-setup';
import AnnotationsGridHandler from 'webapp/d-chart/annotations/grid';
import type DChart from 'webapp/d-chart/d-chart';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import GridRenderer from 'webapp/d-chart/renderer/grid';

export default class LineOrBarChartRenderer extends GridRenderer {
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;

	protected override allowMarkerDotsOnVisualization = true;

	constructor(chart: DChart) {
		super(chart);
		this.initAnnotationsHandler();
	}

	override get visualization() {
		return super.visualization;
	}

	get hasLegend() {
		return !this.visualization.isLegendOff;
	}

	override get themeOptions(): Highcharts.Options {
		let options = super.themeOptions;

		const defs: Record<string, DChartInterface.Def> = {};
		this.getDefs().forEach((def) => {
			defs[def.id] = def;
		});

		options = this.mergeOptions(options, { defs });

		return options;
	}

	get mainGradientDefs(): DChartInterface.Def[] {
		const themeData = this.dChart.themeData,
			customColorClassMap = this.dChart.customColorClassMap,
			blockId = this.visualization.block.id!;
		return [
			...LineOrBarChartRenderer.getAreaGradientDefs(
				themeData,
				customColorClassMap,
				'',
				blockId,
			), // when one primary main
			...LineOrBarChartRenderer.getAreaGradientDefs(
				themeData,
				customColorClassMap,
				'light',
				blockId,
			), // when two primary mains
			...LineOrBarChartRenderer.getAreaGradientDefs(
				themeData,
				customColorClassMap,
				'very-light',
				blockId,
			), // when 3+ primary mains
		];
	}

	get stackedGradientDefs(): DChartInterface.Def[] {
		const themeData = this.dChart.themeData,
			customColorClassMap = this.dChart.customColorClassMap,
			blockId = this.visualization.block.id!;
		return [
			...LineOrBarChartRenderer.getStackedGradientDefs(
				themeData,
				customColorClassMap,
				'',
				blockId,
			),
			...LineOrBarChartRenderer.getStackedGradientDefs(
				themeData,
				customColorClassMap,
				'light',
				blockId,
			),
		];
	}

	get patternBarMissingGradientDefs(): DChartInterface.Def[] {
		const themeData = this.dChart.themeData,
			customColorClassMap = this.dChart.customColorClassMap,
			themeId = themeData.themeId,
			blockConfigColorIndexes = themeData.visualizationConfig.order || [],
			defs: DChartInterface.Def[] = [];

		blockConfigColorIndexes.forEach((colorIndex: number) => {
			const def = LineOrBarChartRenderer.getPatternBarMissingDef(`${themeId}-${colorIndex}`);
			defs.push(def);
		});

		// add also gradient for previous
		const previousPatternBarMissingDef = LineOrBarChartRenderer.getPatternBarMissingDef(
			themeData.baseColor.substr(1),
		);
		defs.push(previousPatternBarMissingDef);

		// custom colors gradients
		Object.entries(customColorClassMap).forEach((entry) => {
			const customClass = entry[1];
			const def = LineOrBarChartRenderer.getPatternBarMissingDef(
				`${this.visualization.block.id}-${customClass}`,
				customClass,
			);
			defs.push(def);
		});

		return defs;
	}

	get forecastsGradientDefs() {
		return LineOrBarChartRenderer.getForecastsGradientDefs();
	}

	get anomalyDefs() {
		return LineOrBarChartRenderer.getAnomalyGradientDefs();
	}

	override getTooltipForPoint(point: DChartPrimaryPoint) {
		const series = point.series;
		if (series && (series.isCompare || series.isGoal || series.isOneOfForecasts)) {
			// exception for certain compare series, to have the same tooltip as their main line,
			// just with this type of compare selected
			const lookingForCompareType =
				this.mmDataSelectsService.getCompareTypeFromDataSelectType(series.type);

			let primarySeries = series.primarySeries;
			// in forecast-only scenarios, the main forecast series is the primary
			if (!primarySeries && (series.isForecastOptimistic || series.isForecastPessimistic)) {
				// in forecast-only scenarios, the main forecast series is the primary (use it as primary for optimistic/pessimistic)
				primarySeries = this.series.find((s) => s.type === DataSelectType.FORECAST) || null;
			} else if (!primarySeries && series.isForecast) {
				// in forecast-only scenarios, the main forecast series is the primary (use itself as primary)
				primarySeries = series;
			}

			const primaryPointOnPrimarySeries = primarySeries?.points.find(
				(item) => item.index === point.index,
			);
			if (primaryPointOnPrimarySeries) {
				const comparePointOnPrimarySeries = primaryPointOnPrimarySeries.comparePoints.find(
					(c) => {
						return c.compareType === lookingForCompareType && c.value === point.value;
					},
				);
				if (comparePointOnPrimarySeries) {
					const html = primaryPointOnPrimarySeries.getTooltipHtmlForPoint(
						comparePointOnPrimarySeries,
					);
					const typeClass = primaryPointOnPrimarySeries.tooltipTypeClass;
					return { html, typeClass };
				}
			}
		}

		return super.getTooltipForPoint(point);
	}

	override updateTheme(): void {
		this.visualization.updateColorIndexes();

		let options = this.themeOptions;

		// merge with data options since they set colorIndex
		options = this.mergeOptions(options, this.dataOptions);

		this.dChart.updateHChart(options);

		this.updateDefs();
	}

	protected override getDefs() {
		return [
			...this.mainGradientDefs,
			...this.stackedGradientDefs,
			...this.patternBarMissingGradientDefs,
			...this.forecastsGradientDefs,
			...this.anomalyDefs,
		];
	}

	protected override initAnnotationsHandler(): void {
		this.annotationsHandler = new AnnotationsGridHandler(this);
	}

	static getAreaGradientDefs(
		themeData: BlockThemeConfig.BlockParsed,
		customColorClassMap: CustomColorMap = {},
		lightness = '',
		customColorIdPrefix = '',
	): DChartInterface.Def[] {
		const themeId = themeData.themeId,
			colors = themeData.colors || [],
			blockConfigColorIndexes = themeData.visualizationConfig.order || [],
			defs: DChartInterface.Def[] = [];

		const addDefForParams = (idPostfix: string, color: string) => {
			let maxOpacity = 0.5,
				id = 'gradient-area-';
			if (lightness) {
				id += `${lightness}-`;
				maxOpacity = lightness === 'light' ? 0.25 : 0.1;
			}
			id += idPostfix;

			defs.push({
				tagName: 'linearGradient',
				id,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						offset: '0%',
						'stop-color': color,
						'stop-opacity': maxOpacity,
					},
					{
						tagName: 'stop',
						offset: '100%',
						'stop-color': color,
						'stop-opacity': 0,
					},
				],
			});

			// inverted gradient for all-negative series (gradient from bottom toward top)
			defs.push({
				tagName: 'linearGradient',
				id: `${id}-reverse`,
				x1: 0,
				y1: 1,
				x2: 0,
				y2: 0,
				children: [
					{
						tagName: 'stop',
						offset: '0%',
						'stop-color': color,
						'stop-opacity': maxOpacity,
					},
					{
						tagName: 'stop',
						offset: '100%',
						'stop-color': color,
						'stop-opacity': 0,
					},
				],
			});
		};

		blockConfigColorIndexes.forEach((colorIndex: number) => {
			addDefForParams(`${themeId}-${colorIndex}`, colors[colorIndex]!);
		});

		Object.entries(customColorClassMap).forEach((entry) => {
			const [hex, customClass] = entry;
			addDefForParams(`${customColorIdPrefix}-${customClass}`, hex);
		});

		return defs;
	}

	static getStackedGradientDefs(
		themeData: BlockThemeConfig.BlockParsed,
		customColorClassMap: CustomColorMap = {},
		lightness: string,
		customColorIdPrefix: string,
	): DChartInterface.Def[] {
		const themeId = themeData.themeId,
			colors = themeData.colors || [],
			blockConfigColorIndexes = themeData.visualizationConfig.order || [],
			defs: DChartInterface.Def[] = [];

		const addDefForParams = (idPostfix: string, color: string) => {
			const id = `gradient-area-stacked${lightness ? `-${lightness}` : ''}-${idPostfix}`,
				minOpacity = lightness ? 0.1 : 0.6,
				maxOpacity = lightness ? 0.3 : 1;

			defs.push({
				tagName: 'linearGradient',
				id,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						offset: '0%',
						'stop-color': color,
						'stop-opacity': minOpacity,
					},
					{
						tagName: 'stop',
						offset: '100%',
						'stop-color': color,
						'stop-opacity': maxOpacity,
					},
				],
			});
		};

		blockConfigColorIndexes.forEach((colorIndex: number) => {
			addDefForParams(`${themeId}-${colorIndex}`, colors[colorIndex]!);
		});

		Object.entries(customColorClassMap).forEach((entry) => {
			const [hex, customClass] = entry;
			addDefForParams(`${customColorIdPrefix}-${customClass}`, hex);
		});

		return defs;
	}

	static getPatternBarMissingDef(idPostfix: string, className = ''): DChartInterface.Def {
		return {
			id: `pattern-bar-missing-${idPostfix}`, // used in highcharts.css
			tagName: 'pattern',
			class: className,
			width: 3,
			height: 3,
			patternTransform: 'rotate(55 0 0)',
			patternUnits: 'userSpaceOnUse',
			children: [
				{
					tagName: 'line',
					x1: 0,
					y1: 0,
					x2: 0,
					y2: 3,
				},
			],
		};
	}

	static getForecastsGradientDefs(): DChartInterface.Def[] {
		const defs: DChartInterface.Def[] = [];

		const forecastOptimisticColor = '#23C18F', // @colorTheme1colors[4]
			forecastPessimisticColor = '#e85a47'; // @colorTheme6colors[2]

		defs.push({
			id: 'gradient-area-forecast-6', // used in highcharts.css
			tagName: 'linearGradient',
			x1: 0,
			y1: 0,
			x2: 0,
			y2: 1,
			children: [
				{
					tagName: 'stop',
					offset: '0%',
					'stop-color': forecastOptimisticColor,
					'stop-opacity': 0.16,
				},
				{
					tagName: 'stop',
					offset: '100%',
					'stop-color': forecastOptimisticColor,
					'stop-opacity': 0.33,
				},
			],
		});

		defs.push({
			id: 'gradient-area-forecast-7', // used in highcharts.css
			tagName: 'linearGradient',
			x1: 0,
			y1: 0,
			x2: 0,
			y2: 1,
			children: [
				{
					tagName: 'stop',
					offset: '0%',
					'stop-color': forecastPessimisticColor,
					'stop-opacity': 0.16,
				},
				{
					tagName: 'stop',
					offset: '100%',
					'stop-color': forecastPessimisticColor,
					'stop-opacity': 0.33,
				},
			],
		});

		return defs;
	}

	static getAnomalyGradientDefs(): DChartInterface.Def[] {
		const defs: DChartInterface.Def[] = [];

		const anomalyColor = 'var(--databox-chart-anomaly-color)';

		defs.push({
			id: 'gradient-area-range-anomaly', // used in highcharts.css
			tagName: 'linearGradient',
			x1: 0,
			y1: 0,
			x2: 0,
			y2: 1,
			children: [
				{
					tagName: 'stop',
					offset: '0%',
					'stop-color': anomalyColor,
					'stop-opacity': 0.1,
				},
				{
					tagName: 'stop',
					offset: '100%',
					'stop-color': anomalyColor,
					'stop-opacity': 0.2,
				},
			],
		});

		return defs;
	}
}
